<template>
  <div class="skeleton-graph-map-box">
    <slot></slot>
  </div>
</template>

<script>
import L from "leaflet";
import 'leaflet/dist/leaflet.css';
import 'leaflet.pm';
import 'leaflet.pm/dist/leaflet.pm.css';
// 确保全局可用
if (typeof window !== 'undefined') {
  window.L = L;
}
import langMap from '@/utils/language';
import { mapState } from 'vuex';
import api from '../../../api';
import mapIntersectionUtil from '@/utils/mapIntersectionUtil';

export default {
  name: 'SkeletonGraphMap',
  props: {
    graphs: {
      type: Array,
      default: () => []
    },
    currentGraphId: {
      type: [String, Number],
      default: null
    },
    editingGraphData: {
      type: Object,
      default: null
    },
    // 新增 props
    addNodeMode: {
      type: Boolean,
      default: false
    },
    autoConnectEnabled: {
      type: Boolean,
      default: false
    },
    autoConnectMode: {
      type: String,
      default: 'sequential' // 'sequential' | 'insert' | 'nearest'
    }
  },
  data() {
    return {
      langMap: langMap,
      map: null,
      graphLayers: {},
      nodeMarkers: {},
      edgeLines: {},
      edgeDecorators: {}, // 箭头装饰器
      editMode: false,
      editingGraphId: null,
      selectedNodes: [],
      connectionMode: false,
      // 电子围栏相关
      fenceLayers: [],
      fenceList: [],
      intersectionLayers: []
    };
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
      mapConfig: state => state.app.leafletConfig,
      areaColorList: state => state.app.mapAreaColor
    })
  },
  mounted() {
    this.initMap();
    // 加载电子围栏和交叉区域
    this.loadFences();
    // 确保地图正确渲染
    this.$nextTick(() => {
      if (this.map) {
        setTimeout(() => {
          this.map.invalidateSize();
          // 通知父组件地图已准备就绪
          this.$emit('map-ready');
        }, 100);
      }
    });
  },
  methods: {
    // 初始化地图
    initMap() {
      const tileLayerOption = this.mapConfig.tileLayerOption;
      this.map = L.map("skeleton_graph_map_id", this.mapConfig.mapInstantiated);
      L.tileLayer(tileLayerOption.url, tileLayerOption).addTo(this.map);

      // 添加绘制控制组件
      this.map.pm.setLang(this.lang || 'zh');
      this.map.pm.addControls({
        position: 'topleft',
        drawPolygon: false,
        drawMarker: false,
        drawCircleMarker: false,
        drawPolyline: false,
        drawRectangle: false,
        drawCircle: false,
        editMode: false,
        dragMode: false,
        cutPolygon: false,
        removalMode: false
      });
    },

    // 渲染所有骨架图
    renderGraphs(graphs) {
      // 清除现有图层
      this.clearAllLayers();

      // 渲染每个骨架图
      graphs.forEach(graph => {
        this.createGraphLayer(graph);
      });

      // 如果有图层，自动缩放到显示所有图层
      if (graphs.length > 0) {
        this.fitBounds();
      }

      // 确保地图正确渲染
      this.$nextTick(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      });
    },

    // 创建单个骨架图图层
    createGraphLayer(graph) {
      if (!graph.nodes || graph.nodes.length === 0) return;

      const graphId = graph.id;
      const isEditing = this.editMode && this.editingGraphId === graphId;
      
      this.graphLayers[graphId] = L.layerGroup().addTo(this.map);
      this.nodeMarkers[graphId] = [];
      this.edgeLines[graphId] = [];

      // 渲染边
      if (graph.edges && graph.edges.length > 0) {
        this.renderEdges(graph.edges, graphId, graph, isEditing);
      }

      // 渲染节点
      this.renderNodes(graph.nodes, graphId, graph, isEditing);
    },

    // 渲染节点
    renderNodes(nodes, graphId, graph, isEditing = true) {
      nodes.forEach(node => {
        this.createNodeMarker(node, graphId, graph, isEditing);
      });
    },

    // 创建节点标记
    createNodeMarker(node, graphId, graph, isEditing = true) {
      // 兼容不同的字段名
      const lat = node.lat || node.latitude;
      const lon = node.lon || node.longitude;
      
      // 使用与任务路径相同的图标样式，但不显示文本
      const markerDefaultBlue = require('@/assets/images/map/mark_default_blue.png');
      
      // 非编辑状态的骨架图使用半透明样式
      const opacity = isEditing ? 1 : 0.4;
      
      const icon = new L.divIcon({
        html: '<img src="' + markerDefaultBlue + '" style="width: 15px;height: 15px;opacity:' + opacity + ';" />',
        className: 'my-div-icon',
        iconAnchor: [7, 7],
      });
      
      const marker = L.marker([lat, lon], {
        icon: icon,
        title: node.id,
        draggable: isEditing, // 只有编辑中的骨架图节点可拖动
        nodeId: node.id,
        graphId: graphId
      });

      marker.addTo(this.graphLayers[graphId]);
      this.nodeMarkers[graphId].push(marker);

      // 只有编辑中的骨架图才响应点击事件
      if (isEditing) {
        // 点击节点
        marker.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          
          // 如果是编辑模式，处理连线
          if (this.editMode && this.connectionMode && this.editingGraphId === graphId) {
            this.handleNodeClickForConnection(marker, graphId);
          } else if (this.editMode && this.editingGraphId === graphId) {
            // 编辑模式下显示节点编辑弹窗
            this.showNodePopup(node.id);
          } else {
            // 否则显示骨架图弹窗
            this.createPopup(graphId, graph, e.latlng);
            this.$emit('graph-clicked', graphId);
          }
        });
      }

      return marker;
    },

    // 渲染边
    renderEdges(edges, graphId, graph, isEditing = true) {
      const nodes = graph.nodes;
      const nodeMap = {};
      nodes.forEach(node => {
        nodeMap[node.id] = node;
      });

      edges.forEach(edge => {
        this.createEdgeLine(edge, nodeMap, graphId, isEditing);
      });
    },

    // 创建边线
    createEdgeLine(edge, nodeMap, graphId, isEditing = true) {
      const fromNodeId = edge.fromNodeId || edge.from;
      const toNodeId = edge.toNodeId || edge.to;
      const fromNode = nodeMap[fromNodeId];
      const toNode = nodeMap[toNodeId];

      if (!fromNode || !toNode) return;

      // 兼容不同的字段名
      const fromLat = fromNode.lat || fromNode.latitude;
      const fromLon = fromNode.lon || fromNode.longitude;
      const toLat = toNode.lat || toNode.latitude;
      const toLon = toNode.lon || toNode.longitude;

      const points = [
        [fromLat, fromLon],
        [toLat, toLon]
      ];

      // bidirectional: 1=双向，0=单向
      const color = edge.bidirectional === 0 ? '#ff9800' : '#1890ff';
      
      // 非编辑状态的骨架图使用半透明样式
      const opacity = isEditing ? 0.7 : 0.3;
      
      const line = L.polyline(points, {
        color: color,
        weight: 3,
        opacity: opacity,
        edgeId: `${fromNodeId}-${toNodeId}`,
        graphId: graphId,
        interactive: isEditing // 只有编辑中的骨架图连线可交互
      });

      line.addTo(this.graphLayers[graphId]);
      this.edgeLines[graphId].push(line);

      // 添加箭头装饰器
      try {
        console.log('Checking L.polylineDecorator:', typeof L.polylineDecorator);
        console.log('Edge bidirectional:', edge.bidirectional);
        
        if (L.polylineDecorator) {
          let patterns;
          
          if (edge.bidirectional === 0) {
            // 单向：箭头在终点
            patterns = [
              {
                offset: '100%',
                repeat: 0,
                symbol: L.Symbol.arrowHead({
                  pixelSize: 10,
                  polygon: false,
                  pathOptions: {
                    stroke: true,
                    color: color,
                    weight: 2,
                    opacity: opacity
                  }
                })
              }
            ];
          } else {
            // 双向：两个箭头在中间，相对方向
            patterns = [
              {
                offset: '50%',
                repeat: 0,
                symbol: L.Symbol.arrowHead({
                  pixelSize: 10,
                  polygon: false,
                  pathOptions: {
                    stroke: true,
                    color: color,
                    weight: 2,
                    opacity: opacity
                  }
                })
              },
              {
                offset: '50%',
                repeat: 0,
                symbol: L.Symbol.arrowHead({
                  pixelSize: 10,
                  polygon: false,
                  pathOptions: {
                    stroke: true,
                    color: color,
                    weight: 2,
                    opacity: opacity
                  }
                }),
                // 反向箭头 - 通过旋转180度实现
                heading: 180
              }
            ];
          }

          const decorator = L.polylineDecorator(line, {
            patterns: patterns
          });
          
          decorator.addTo(this.graphLayers[graphId]);
          console.log('Arrow decorator added successfully');

          // 保存装饰器引用，以便后续删除
          if (!this.edgeDecorators[graphId]) {
            this.edgeDecorators[graphId] = [];
          }
          this.edgeDecorators[graphId].push(decorator);
        } else {
          console.warn('L.polylineDecorator is not available');
        }
      } catch (error) {
        console.error('Failed to add arrow decorator:', error);
      }

      return line;
    },

    // 创建弹窗
    createPopup(graphId, graph, latlng) {
      const nodeCount = graph.nodes ? graph.nodes.length : 0;
      const edgeCount = graph.edges ? graph.edges.length : 0;

      const content = `
        <div class="skeleton-graph-popup">
          <h4>${graph.name}</h4>
          <p>${this.langMap.belongStation[this.lang]}：${graph.stationName || '-'}</p>
          <p>${this.langMap.nodeCount[this.lang]}：${nodeCount}</p>
          <p>${this.langMap.edgeCount[this.lang]}：${edgeCount}</p>
          <div style="margin-top: 10px;">
            <button onclick="window.editGraphPoints('${graphId}')" class="popup-btn">
              ${this.langMap.editPoints[this.lang]}
            </button>
            <button onclick="window.viewTopology('${graphId}')" class="popup-btn" style="margin-left: 8px;">
              ${this.langMap.viewTopology[this.lang]}
            </button>
          </div>
        </div>
      `;

      const popup = L.popup({ offset: [0, -15] })
        .setLatLng(latlng)
        .setContent(content);

      popup.openOn(this.map);
    },

    // 定位到指定骨架图
    focusOnGraph(graphId) {
      const graph = this.graphs.find(g => g.id === graphId);
      if (!graph || !graph.nodes || graph.nodes.length === 0) return;

      // 兼容不同的字段名
      const bounds = L.latLngBounds(
        graph.nodes.map(node => {
          const lat = node.lat || node.latitude;
          const lon = node.lon || node.longitude;
          return [lat, lon];
        })
      );

      this.map.fitBounds(bounds, { padding: [50, 50] });

      // 触发弹窗显示
      const centerNode = graph.nodes[0];
      const centerLat = centerNode.lat || centerNode.latitude;
      const centerLon = centerNode.lon || centerNode.longitude;
      this.createPopup(graphId, graph, [centerLat, centerLon]);
    },

    // 自动缩放到显示所有图层
    fitBounds() {
      const allPoints = [];
      this.graphs.forEach(graph => {
        if (graph.nodes) {
          graph.nodes.forEach(node => {
            const lat = node.lat || node.latitude;
            const lon = node.lon || node.longitude;
            allPoints.push([lat, lon]);
          });
        }
      });

      if (allPoints.length > 0) {
        const bounds = L.latLngBounds(allPoints);
        this.map.fitBounds(bounds, { padding: [50, 50] });
      }
    },

    // 清除所有图层
    clearAllLayers() {
      Object.keys(this.graphLayers).forEach(graphId => {
        this.map.removeLayer(this.graphLayers[graphId]);
      });
      this.graphLayers = {};
      this.nodeMarkers = {};
      this.edgeLines = {};
      this.edgeDecorators = {};
    },

    // 清除指定图层
    clearGraphLayer(graphId) {
      if (this.graphLayers[graphId]) {
        this.map.removeLayer(this.graphLayers[graphId]);
        delete this.graphLayers[graphId];
        delete this.nodeMarkers[graphId];
        delete this.edgeLines[graphId];
        delete this.edgeDecorators[graphId];
      }
    },

    // 加载电子围栏数据
    loadFences() {
      api.getData('task-server/fence/list', this).then((res) => {
        if (res.state != 0 && res.data) {
          this.fenceList = res.data;
          this.initTargetArea(this.fenceList);
        }
      }).catch(() => {
        // 静默失败，不影响骨架图功能
      });
    },

    // 初始化绘制区域（电子围栏）
    initTargetArea(areaList) {
      const dataList = areaList || [];
      this.fenceLayers = []; // 清空现有图层

      dataList.forEach((item) => {
        if (item.area !== null) {
          let color = this.areaColorList[item.type];

          let areas = JSON.parse(item.area);
          let areaTemp = [];
          areas.forEach((item1) => {
            let point = [item1.lat, item1.lon];
            areaTemp.push(point);
          });

          let polygon = L.polygon(areaTemp, {
            color: color,
            id: item.id,
            type: item.type,
            stationId: item.stationId,
            layerPriority: item.layerPriority || 0,
            interactive: false // 不可交互，作为背景显示
          }).addTo(this.map);

          // 保存到图层数组中
          this.fenceLayers.push(polygon);
        }
      });

      // 计算并显示交叉区域
      this.findAndDisplayIntersections();
    },

    // 查找并显示交叉区域
    findAndDisplayIntersections() {
      // 使用工具类计算并显示交叉区域
      this.intersectionLayers = mapIntersectionUtil.findAndDisplayIntersections(
        this.map,
        this.fenceLayers,
        this.intersectionLayers
      );
    },

    // 启用编辑模式
    enableEditMode(graphId) {
      this.editMode = true;
      this.editingGraphId = graphId;
      this.connectionMode = true;
      this.selectedNodes = [];

      // 使节点可拖拽
      this.makeNodesDraggable(graphId);

      // 为边添加点击删除功能
      this.makeEdgesDeletable(graphId);
    },

    // 禁用编辑模式
    disableEditMode() {
      this.editMode = false;
      this.editingGraphId = null;
      this.connectionMode = false;
      this.selectedNodes = [];

      // 恢复节点不可拖拽
      if (this.editingGraphId && this.nodeMarkers[this.editingGraphId]) {
        this.nodeMarkers[this.editingGraphId].forEach(marker => {
          marker.dragging.disable();
        });
      }
    },

    // 使节点可拖拽
    makeNodesDraggable(graphId) {
      if (!this.nodeMarkers[graphId]) return;

      this.nodeMarkers[graphId].forEach(marker => {
        marker.dragging.enable();
        
        // 拖动中：实时更新连线
        marker.on('drag', (e) => {
          const nodeId = marker.options.nodeId;
          this.updateEdgesForNode(nodeId, graphId);
        });
        
        marker.on('dragend', (e) => {
          const newLatLng = e.target.getLatLng();
          const nodeId = marker.options.nodeId;
          
          // 触发节点拖动事件，传递 GCJ02 坐标（地图坐标）
          this.$emit('node-dragged', {
            graphId: graphId,
            nodeId: nodeId,
            lat: newLatLng.lat,
            lng: newLatLng.lng
          });

          // 重新渲染该图的边
          this.rerenderGraphEdges(graphId);
        });
      });
    },

    // 使边可删除
    makeEdgesDeletable(graphId) {
      if (!this.edgeLines[graphId]) return;

      this.edgeLines[graphId].forEach(line => {
        line.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          
          if (this.editMode && this.editingGraphId === graphId) {
            const edgeId = line.options.edgeId;
            
            // 显示连线弹窗
            this.showEdgePopup(edgeId);
          }
        });
      });
    },

    // 处理节点点击（连线模式）
    handleNodeClickForConnection(marker, graphId) {
      if (!this.editMode || !this.connectionMode) return;
      if (this.editingGraphId !== graphId) return;

      const nodeId = marker.options.nodeId;

      // 添加到选中节点列表
      if (this.selectedNodes.length === 0) {
        this.selectedNodes.push(nodeId);
        this.highlightNode(marker);
        this.$Message.info(this.langMap.selectSecondNode[this.lang]);
      } else if (this.selectedNodes.length === 1) {
        if (this.selectedNodes[0] === nodeId) {
          // 点击同一个节点，取消选择
          this.selectedNodes = [];
          this.clearHighlight();
          return;
        }

        this.selectedNodes.push(nodeId);
        
        // 创建连线（智能路径）
        this.createSmartConnection(this.selectedNodes[0], this.selectedNodes[1], graphId);
        
        // 重置选择
        this.selectedNodes = [];
        this.clearHighlight();
      }
    },

    // 创建智能路径连线
    createSmartConnection(nodeAId, nodeBId, graphId) {
      // 检查是否已存在直接边
      const exists = this.edgeExists(nodeAId, nodeBId, graphId);
      
      if (exists) {
        this.$Message.warning(this.langMap.edgeAlreadyExists[this.lang]);
        return;
      }

      // 查找最短路径
      const path = this.findShortestPath(nodeAId, nodeBId, graphId);

      if (path && path.length > 2) {
        // 存在路径，创建路径上所有缺失的边
        const newEdges = [];
        for (let i = 0; i < path.length - 1; i++) {
          if (!this.edgeExists(path[i], path[i + 1], graphId)) {
            const edge = this.createDirectEdge(path[i], path[i + 1], graphId);
            if (edge) {
              newEdges.push(edge);
            }
          }
        }

        if (newEdges.length > 0) {
          this.$emit('edges-created', newEdges);
        } else {
          this.$Message.info(this.langMap.allEdgesExist[this.lang]);
        }
      } else {
        // 不存在路径或只有两个节点，创建直接边
        const edge = this.createDirectEdge(nodeAId, nodeBId, graphId);
        if (edge) {
          this.$emit('edge-created', edge);
        }
      }
    },

    // 查找最短路径（BFS）
    findShortestPath(startId, endId, graphId) {
      const graph = this.graphs.find(g => g.id === graphId);
      if (!graph || !graph.edges) return null;

      // 构建邻接表
      const adjacencyList = new Map();
      graph.nodes.forEach(node => {
        adjacencyList.set(node.id, []);
      });

      graph.edges.forEach(edge => {
        const fromNodeId = edge.fromNodeId || edge.from;
        const toNodeId = edge.toNodeId || edge.to;
        
        adjacencyList.get(fromNodeId).push(toNodeId);
        if (edge.bidirectional === 1) { // 1=双向
          adjacencyList.get(toNodeId).push(fromNodeId);
        }
      });

      // BFS 查找最短路径
      const queue = [[startId]];
      const visited = new Set([startId]);

      while (queue.length > 0) {
        const path = queue.shift();
        const current = path[path.length - 1];

        if (current === endId) {
          return path;
        }

        const neighbors = adjacencyList.get(current) || [];
        for (const neighbor of neighbors) {
          if (!visited.has(neighbor)) {
            visited.add(neighbor);
            queue.push([...path, neighbor]);
          }
        }
      }

      return null; // 未找到路径
    },

    // 检查边是否存在（检查编辑中的数据）
    edgeExists(nodeAId, nodeBId, graphId) {
      // 优先检查编辑中的数据（从 props 传入）
      if (this.editingGraphData && this.editingGraphData.id === graphId && this.editingGraphData.edges) {
        const exists = this.editingGraphData.edges.some(edge => {
          const fromNodeId = edge.fromNodeId || edge.from;
          const toNodeId = edge.toNodeId || edge.to;
          
          return (fromNodeId === nodeAId && toNodeId === nodeBId) ||
                 (edge.bidirectional === 1 && fromNodeId === nodeBId && toNodeId === nodeAId); // 1=双向
        });
        
        if (exists) {
          return true;
        }
      }

      // 如果编辑数据中不存在，再检查原始数据
      const graph = this.graphs.find(g => g.id === graphId);
      if (!graph || !graph.edges) {
        return false;
      }

      return graph.edges.some(edge => {
        const fromNodeId = edge.fromNodeId || edge.from;
        const toNodeId = edge.toNodeId || edge.to;
        
        return (fromNodeId === nodeAId && toNodeId === nodeBId) ||
               (edge.bidirectional === 1 && fromNodeId === nodeBId && toNodeId === nodeAId); // 1=双向
      });
    },

    // 创建直接边
    createDirectEdge(nodeAId, nodeBId, graphId) {
      const graph = this.graphs.find(g => g.id === graphId);
      if (!graph) return null;

      const nodeMap = {};
      graph.nodes.forEach(node => {
        nodeMap[node.id] = node;
      });

      const nodeA = nodeMap[nodeAId];
      const nodeB = nodeMap[nodeBId];
      
      if (!nodeA || !nodeB) return null;

      // 计算两点之间的距离（米）
      const latA = nodeA.lat || nodeA.latitude;
      const lonA = nodeA.lon || nodeA.longitude;
      const latB = nodeB.lat || nodeB.latitude;
      const lonB = nodeB.lon || nodeB.longitude;
      
      const distance = this.calculateDistance(latA, lonA, latB, lonB);

      const edge = {
        fromNodeId: nodeAId,
        toNodeId: nodeBId,
        bidirectional: 1, // 1=双向，0=单向
        weight: distance // 两点之间的距离（米）
      };

      // 在地图上渲染边
      this.createEdgeLine(edge, nodeMap, graphId);

      return edge;
    },

    // 计算两点之间的距离（米）- 使用 Haversine 公式
    calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371000; // 地球半径（米）
      const dLat = this.toRadians(lat2 - lat1);
      const dLon = this.toRadians(lon2 - lon1);
      const a = 
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c;
      return Math.round(distance * 100) / 100; // 保留2位小数
    },

    // 角度转弧度
    toRadians(degrees) {
      return degrees * Math.PI / 180;
    },

    // 重新渲染图的边
    rerenderGraphEdges(graphId) {
      const graph = this.graphs.find(g => g.id === graphId);
      if (!graph || !graph.edges) return;

      const isEditing = this.editMode && this.editingGraphId === graphId;

      // 清除现有边
      if (this.edgeLines[graphId]) {
        this.edgeLines[graphId].forEach(line => {
          this.map.removeLayer(line);
        });
        this.edgeLines[graphId] = [];
      }

      // 重新渲染
      this.renderEdges(graph.edges, graphId, graph, isEditing);
      
      // 重新添加删除功能
      if (this.editMode && isEditing) {
        this.makeEdgesDeletable(graphId);
      }
    },

    // 高亮节点
    highlightNode(marker) {
      const markerDefaultBlue = require('@/assets/images/map/mark_default_blue.png');
      const icon = new L.divIcon({
        html: '<img src="' + markerDefaultBlue + '" style="width: 15px;height: 15px;filter: hue-rotate(90deg);" />',
        className: 'my-div-icon',
        iconAnchor: [7, 7],
      });
      marker.setIcon(icon);
    },

    // 清除高亮
    clearHighlight() {
      const markerDefaultBlue = require('@/assets/images/map/mark_default_blue.png');
      if (this.editingGraphId && this.nodeMarkers[this.editingGraphId]) {
        this.nodeMarkers[this.editingGraphId].forEach(marker => {
          const icon = new L.divIcon({
            html: '<img src="' + markerDefaultBlue + '" style="width: 15px;height: 15px;" />',
            className: 'my-div-icon',
            iconAnchor: [7, 7],
          });
          marker.setIcon(icon);
        });
      }
    },

    // 启用添加节点模式
    enableAddNodeMode() {
      this.addNodeMode = true;
      this.map.getContainer().style.cursor = 'crosshair';
      
      // 监听地图点击事件
      this.map.on('click', this.handleMapClickForAddNode);
      
      this.$Message.info(this.langMap.skeletonGraphMapAddModeMsg[this.lang]);
    },

    // 禁用添加节点模式
    disableAddNodeMode() {
      this.addNodeMode = false;
      this.map.getContainer().style.cursor = '';
      
      // 移除地图点击事件监听
      this.map.off('click', this.handleMapClickForAddNode);
    },

    // 处理地图点击添加节点
    handleMapClickForAddNode(e) {
      if (!this.addNodeMode) return;
      
      // 触发 map-clicked 事件，传递 GCJ02 坐标（地图原生坐标）
      this.$emit('map-clicked', {
        lat: e.latlng.lat,
        lng: e.latlng.lng
      });
      
      // 禁用添加模式
      this.disableAddNodeMode();
    },

    // 显示节点弹窗
    showNodePopup(nodeId) {
      // 查找节点标记
      let marker = null;
      let graphId = null;
      
      for (const gId in this.nodeMarkers) {
        const found = this.nodeMarkers[gId].find(m => m.options.nodeId === nodeId);
        if (found) {
          marker = found;
          graphId = gId;
          break;
        }
      }
      
      if (!marker) return;
      
      const graph = this.graphs.find(g => g.id === graphId);
      if (!graph) return;
      
      const node = graph.nodes.find(n => n.id === nodeId);
      if (!node) return;
      
      // 创建节点编辑弹窗
      const content = `
        <div class="node-edit-popup">
          <h4>${this.langMap.skeletonGraphNode[this.lang]} ${node.id}</h4>
          <div class="coord-row">
            <label>${this.langMap.skeletonGraphWgs84Lon[this.lang]}：</label>
            <span class="coord-value" data-field="lon" data-node-id="${node.id}">${node.wgs84Lon || node.longitude}</span>
            <i class="ivu-icon ivu-icon-md-create edit-icon"></i>
          </div>
          <div class="coord-row">
            <label>${this.langMap.skeletonGraphWgs84Lat[this.lang]}：</label>
            <span class="coord-value" data-field="lat" data-node-id="${node.id}">${node.wgs84Lat || node.latitude}</span>
            <i class="ivu-icon ivu-icon-md-create edit-icon"></i>
          </div>
          <button class="delete-btn">${this.langMap.delete[this.lang]}</button>
        </div>
      `;
      
      const popup = L.popup()
        .setLatLng(marker.getLatLng())
        .setContent(content)
        .openOn(this.map);
      
      // 绑定编辑事件
      this.$nextTick(() => {
        this.bindPopupEditEvents(popup, node, graphId);
      });
    },

    // 绑定弹窗编辑事件
    bindPopupEditEvents(popup, node, graphId) {
      const popupElement = popup.getElement();
      if (!popupElement) return;
      
      // 点击编辑图标
      const editIcons = popupElement.querySelectorAll('.edit-icon');
      editIcons.forEach(icon => {
        icon.addEventListener('click', (e) => {
          const row = e.target.closest('.coord-row');
          const valueSpan = row.querySelector('.coord-value');
          const field = valueSpan.dataset.field;
          const currentValue = valueSpan.textContent;
          
          // 转换为输入框
          const input = document.createElement('input');
          input.type = 'number';
          input.value = currentValue;
          input.className = 'coord-input';
          input.step = '0.000001';
          
          valueSpan.replaceWith(input);
          input.focus();
          
          // 失焦时保存
          input.addEventListener('blur', () => {
            const newValue = parseFloat(input.value);
            const updatedNode = { ...node };
            
            if (field === 'lon') {
              updatedNode.wgs84Lon = newValue;
            } else if (field === 'lat') {
              updatedNode.wgs84Lat = newValue;
            }
            
            // 触发节点更新事件
            this.$emit('node-updated', {
              graphId: graphId,
              node: updatedNode
            });
            
            popup.remove();
          });
        });
      });
      
      // 删除按钮
      const deleteBtn = popupElement.querySelector('.delete-btn');
      if (deleteBtn) {
        deleteBtn.addEventListener('click', () => {
          this.$Modal.confirm({
            title: this.langMap.confirm[this.lang],
            content: this.langMap.skeletonGraphDeleteNodeContent[this.lang],
            onOk: () => {
              this.$emit('delete-node', {
                graphId: graphId,
                nodeId: node.id
              });
              popup.remove();
            }
          });
        });
      }
    },

    // 显示连线弹窗
    showEdgePopup(edgeId) {
      // 查找连线
      let line = null;
      let graphId = null;
      
      for (const gId in this.edgeLines) {
        const found = this.edgeLines[gId].find(l => l.options.edgeId === edgeId);
        if (found) {
          line = found;
          graphId = gId;
          break;
        }
      }
      
      if (!line) return;
      
      const graph = this.graphs.find(g => g.id === graphId);
      if (!graph) return;
      
      const [fromNodeId, toNodeId] = edgeId.split('-');
      const edge = graph.edges.find(e => {
        const fId = e.fromNodeId || e.from;
        const tId = e.toNodeId || e.to;
        return fId == fromNodeId && tId == toNodeId;
      });
      
      if (!edge) return;
      
      const direction = edge.bidirectional === 1 ? this.langMap.skeletonGraphBidirectional[this.lang] : this.langMap.skeletonGraphUnidirectional[this.lang];
      const distance = edge.weight ? edge.weight.toFixed(2) : this.langMap.skeletonGraphUnknown[this.lang];
      
      const content = `
        <div class="edge-popup">
          <h4>${this.langMap.skeletonGraphEdgeInfo[this.lang]}</h4>
          <p><strong>${this.langMap.skeletonGraphFromNode[this.lang]}：</strong>${fromNodeId}</p>
          <p><strong>${this.langMap.skeletonGraphToNode[this.lang]}：</strong>${toNodeId}</p>
          <p><strong>${this.langMap.skeletonGraphEdgeDirection[this.lang]}：</strong>${direction}</p>
          <p><strong>${this.langMap.skeletonGraphDistance[this.lang]}：</strong>${distance} ${this.langMap.skeletonGraphMeter[this.lang]}</p>
          <button class="delete-btn">${this.langMap.delete[this.lang]}</button>
        </div>
      `;
      
      const latlng = line.getCenter();
      const popup = L.popup()
        .setLatLng(latlng)
        .setContent(content)
        .openOn(this.map);
      
      // 绑定删除事件
      this.$nextTick(() => {
        const popupElement = popup.getElement();
        if (!popupElement) return;
        
        const deleteBtn = popupElement.querySelector('.delete-btn');
        if (deleteBtn) {
          deleteBtn.addEventListener('click', () => {
            this.$Modal.confirm({
              title: this.langMap.confirm[this.lang],
              content: this.langMap.skeletonGraphDeleteEdgeContent[this.lang],
              onOk: () => {
                this.$emit('delete-edge', {
                  graphId: graphId,
                  edgeId: edgeId
                });
                popup.remove();
              }
            });
          });
        }
      });
    },

    // 更新节点位置
    updateNodePosition(nodeId, latlng) {
      // 查找节点标记
      let marker = null;
      let graphId = null;
      
      for (const gId in this.nodeMarkers) {
        const found = this.nodeMarkers[gId].find(m => m.options.nodeId === nodeId);
        if (found) {
          marker = found;
          graphId = gId;
          break;
        }
      }
      
      if (!marker) return;
      
      // 更新标记位置
      marker.setLatLng([latlng.lat, latlng.lng]);
      
      // 更新所有关联连线位置
      this.updateEdgesForNode(nodeId, graphId);
    },

    // 更新节点关联的所有连线
    updateEdgesForNode(nodeId, graphId) {
      if (!this.edgeLines[graphId]) return;
      
      const graph = this.graphs.find(g => g.id === graphId);
      if (!graph) return;
      
      const nodeMap = {};
      graph.nodes.forEach(node => {
        nodeMap[node.id] = node;
      });
      
      // 找到所有与该节点相关的连线
      this.edgeLines[graphId].forEach(line => {
        const edgeId = line.options.edgeId;
        const [fromNodeId, toNodeId] = edgeId.split('-');
        
        if (fromNodeId == nodeId || toNodeId == nodeId) {
          // 找到起始和目标节点的标记
          const fromMarker = this.nodeMarkers[graphId].find(m => m.options.nodeId == fromNodeId);
          const toMarker = this.nodeMarkers[graphId].find(m => m.options.nodeId == toNodeId);
          
          if (fromMarker && toMarker) {
            line.setLatLngs([
              fromMarker.getLatLng(),
              toMarker.getLatLng()
            ]);
          }
        }
      });
    }
  },
  watch: {
    graphs: {
      handler(newGraphs) {
        this.renderGraphs(newGraphs);
      },
      deep: true
    },
    currentGraphId(newId) {
      if (newId) {
        this.focusOnGraph(newId);
      }
    }
  }
};
</script>

<style scoped>
.skeleton-graph-map-box {
  height: 100%;
  position: relative;
}

.skeleton-graph-map-box >>> .skeleton-graph-popup h4 {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.skeleton-graph-map-box >>> .skeleton-graph-popup p {
  margin: 4px 0;
  font-size: 14px;
  color: #666;
}

.skeleton-graph-map-box >>> .popup-btn {
  padding: 4px 12px;
  font-size: 12px;
  color: #fff;
  background-color: #1890ff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.skeleton-graph-map-box >>> .popup-btn:hover {
  background-color: #40a9ff;
}

.skeleton-graph-map-box >>> .node-edit-popup h4,
.skeleton-graph-map-box >>> .edge-popup h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.skeleton-graph-map-box >>> .node-edit-popup .coord-row {
  display: flex;
  align-items: center;
  margin: 8px 0;
  font-size: 14px;
}

.skeleton-graph-map-box >>> .node-edit-popup .coord-row label {
  min-width: 90px;
  color: #666;
}

.skeleton-graph-map-box >>> .node-edit-popup .coord-row .coord-value {
  flex: 1;
  color: #333;
}

.skeleton-graph-map-box >>> .node-edit-popup .coord-row .edit-icon {
  margin-left: 8px;
  color: #1890ff;
  cursor: pointer;
  font-size: 16px;
}

.skeleton-graph-map-box >>> .node-edit-popup .coord-row .edit-icon:hover {
  color: #40a9ff;
}

.skeleton-graph-map-box >>> .node-edit-popup .coord-input {
  flex: 1;
  padding: 4px 8px;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  font-size: 14px;
}

.skeleton-graph-map-box >>> .node-edit-popup .delete-btn,
.skeleton-graph-map-box >>> .edge-popup .delete-btn {
  margin-top: 12px;
  padding: 4px 12px;
  font-size: 12px;
  color: #fff;
  background-color: #ed4014;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.skeleton-graph-map-box >>> .node-edit-popup .delete-btn:hover,
.skeleton-graph-map-box >>> .edge-popup .delete-btn:hover {
  background-color: #f16643;
}

.skeleton-graph-map-box >>> .edge-popup p {
  margin: 6px 0;
  font-size: 14px;
  color: #666;
}

.skeleton-graph-map-box >>> .edge-popup p strong {
  color: #333;
}
</style>
